import { useEffect, useRef, useState } from 'react';
import { api, newOperationId } from '../api/client';
import type { OperationProgress, StepResult } from '../api/types';

/**
 * 다단계 작업 진행 폴링 (docs/04 TxProgressModal).
 *
 * 서버 호출은 원자적이라 응답 전에는 중간 상태를 알 수 없다. 대신 서버가 단계 진행을
 * `operation_steps` 에 기록하므로, 작업 식별자(opId)를 미리 만들어 요청 헤더로 보내고
 * 진행 중에는 `GET /operations/:opId` 를 짧은 주기로 조회해 실시간으로 표시한다.
 *
 * 사용:
 *   const prog = useOperationProgress();
 *   const opId = prog.start();                        // 요청 직전
 *   await api.post(url, body, { 'X-Operation-Id': opId });
 *   prog.stop(res.steps);                             // 응답의 확정 단계로 마무리
 */
export function useOperationProgress(intervalMs = 1000) {
  const [steps, setSteps] = useState<StepResult[] | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => clear, []);

  /** 새 작업 시작 — opId 를 만들고 폴링을 건다 */
  const start = (): string => {
    clear();
    setSteps(null);
    const opId = newOperationId();
    timer.current = setInterval(() => {
      // 폴링 실패는 무시한다(작업은 계속 진행 중이며 최종 결과는 응답으로 받는다)
      api.get<OperationProgress>(`/operations/${opId}`)
        .then((r) => { if (r.steps?.length) setSteps(r.steps); })
        .catch(() => undefined);
    }, intervalMs);
    return opId;
  };

  /** 폴링 종료 — 응답이 준 확정 단계가 있으면 그것으로 덮어쓴다 */
  const stop = (finalSteps?: StepResult[] | null): void => {
    clear();
    if (finalSteps?.length) setSteps(finalSteps);
  };

  return { steps, start, stop };
}
